import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Form, Field, Group } from '../../common/index';
import FieldWrapper from '../../common/FieldWrapper';
import { CREATE_BOOKLET_SERIES, UPDATE_BOOKLET_SERIES, GET_BOOKLETS_WITH_SERIES } from '../../queries/bookletSeries';
import { IoInformationCircle } from 'react-icons/io5';
import Tooltip from '../../common/Tooltip';

function BookletSeriesForm(props) {
  const { content, onComplete, zoneId, associations, zoneAssociations } = props;

  const [sContent, setSContent] = useState(content);

  useEffect(() => {
    setSContent({
      ...content,
      zoneId: zoneId,
    });
  }, [content, zoneId]);

  const ss = (b) => setSContent({ ...sContent, ...b });

  return (
        <Form
          sContent={sContent}
          parent="zoneId"
          get={GET_BOOKLETS_WITH_SERIES}
          add={CREATE_BOOKLET_SERIES}
          update={UPDATE_BOOKLET_SERIES}
          onComplete={onComplete}
        >
          <Group title="Allmänt">
            <Field
              type="text"
              req
              name="name"
              title="Serienamn *"
              value={sContent && sContent.name}
              onChange={ss}
            />
            <Field
              type="number"
              name="price"
              title="Pris (kr, inkl. moms)"
              value={sContent && sContent.price}
              onChange={ss}
            />
            <Field
              type="number"
              name="defaultValidityMonths"
              title="Normal giltighetstid (månader)"
              value={sContent && sContent.defaultValidityMonths}
              onChange={ss}
            />
          </Group>
          <Group title="Administratörer">
            <Field
              type="admins"
              x2
              name="adminEmails"
              title="Administratörer"
              value={sContent && sContent.adminEmails}
              onChange={ss}
            />
          </Group>
          <Group title={
            <>
              Huvudförening
              <Tooltip content="Huvudföreningen fästs längst upp i listan av föreningar inom zonen, har möjlighet att få stödandel och får tillgång till unikt material i Mina Sidor.">
                <IoInformationCircle size={16} />
              </Tooltip>
            </>
          }>
            <FieldWrapper x2 name="mainAssociation" title="Huvudförening">
              <Select
                options={zoneAssociations && zoneAssociations
                  .map((a) => ({
                    value: a._id,
                    label: a._id === sContent?.mainAssociationId ? `${a.name} (Vald)` : a.name,
                    isSelected: a._id === sContent?.mainAssociationId
                  }))
                  .sort((a, b) => {
                    if (a.isSelected && !b.isSelected) return -1;
                    if (!a.isSelected && b.isSelected) return 1;
                    return a.label.localeCompare(b.label);
                  })
                }
                value={sContent && sContent.mainAssociationId && (() => {
                  const association = zoneAssociations?.find((a) => a._id === sContent.mainAssociationId);
                  return {
                    value: sContent.mainAssociationId,
                    label: association ? association.name : ''
                  };
                })()}
                placeholder="Välj huvudförening..."
                onChange={(selected) => ss({ mainAssociationId: selected?.value || null })}
                isClearable
              />
            </FieldWrapper>
          </Group>
          <Group title="Försäljning">
            <FieldWrapper name="associations" title="Säljbart av föreningar" x2>
              <Select
                isMulti
                options={associations && associations.map((a) => ({ value: a._id, label: a.name }))}
                value={sContent && sContent.purchasableThroughAssociations && sContent.purchasableThroughAssociations.map((id) => {
                  const association = associations?.find((a) => a._id === id);
                  return {
                    value: id,
                    label: association ? association.name : ''
                  };
                })}
                placeholder="Välj föreningar..."
                onChange={(selected) => ss({ purchasableThroughAssociations: selected?.map((s) => s.value) })}
              />
            </FieldWrapper>
          </Group>
          <Group title={
            <Fragment>
              Total andel
              <Tooltip content="Summan som alltid delas ut för varje sålt häfte.">
                <IoInformationCircle size={16} />
              </Tooltip>
            </Fragment>
          }>
            <Field x2 type="number" name="totalCommission" title="Total andel (kr, inkl. moms)" value={sContent && sContent.totalCommission} onChange={ss} />
          </Group>
          <Group title="Stödandel">
            <Field
              type="number"
              name="supportCommissionToMainAssociation"
              title={
                <>
                  Stödandel till huvudförening (kr, inkl. moms)
                  <Tooltip content="Andel av total andel som går till huvudföreningen när köpet sker via en förening.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              value={sContent && sContent.supportCommissionToMainAssociation}
              onChange={ss}
            />
            <Field
              type="number"
              name="supportCommissionToAssociation"
              title={
                <>
                  Stödandel till förening (kr, inkl. moms)
                  <Tooltip content="Andel av total andel som eventuellt går till en förening när köpet sker via huvudförening.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              value={sContent && sContent.supportCommissionToAssociation}
              onChange={ss}
            />
          </Group>
          <Group title="Utseende">
            <Field
              type="color"
              name="colorLeft"
              title="Färg på vänster sida"
              value={sContent && sContent.colorLeft}
              onChange={ss}
            />
            <Field
              type="color"
              name="colorRight"
              title="Färg på höger sida"
              value={sContent && sContent.colorRight}
              onChange={ss}
            />
            <Field
              type="color"
              name="colorFont"
              title="Färg på text"
              value={sContent && sContent.colorFont}
              onChange={ss}
            />
            <Field
              type="color"
              name="colorSalesMaterial"
              title="Färg på försäljningsmaterial"
              value={sContent && sContent.colorSalesMaterial}
              onChange={ss}
            />
            <Field
              type="image"
              name="logoURL"
              title="Logotyp (max 1MB)"
              value={sContent && sContent.logoURL}
              onChange={ss}
            />
          </Group>
        </Form>
  );
}

BookletSeriesForm.propTypes = {
  content: PropTypes.object,
  onComplete: PropTypes.func.isRequired,
  zoneId: PropTypes.string.isRequired,
  associations: PropTypes.array,
  zoneAssociations: PropTypes.array,
};

BookletSeriesForm.defaultProps = {
  content: null,
  associations: [],
  zoneAssociations: [],
};

export default BookletSeriesForm;
